import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import styles from './LayoutHeader.module.scss';
import classnames from 'classnames';

import Link from 'next/link';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { NavMenuButton } from 'src/modules/layout/components/header/components/NavMenuButton';
import { NavMenu } from './components/NavMenu';
import SearchDrawer from 'src/modules/search/components/searchDrawer/SearchDrawer';
import { FlagWithLocale, FlagWithLocaleFlagType } from './components/FlagWithLocale';
import { NavMainMenu, NavMainMenuLink } from './components/NavMainMenu';
import { Media } from 'react-breakpoints';
import { BaseStore } from 'src/store';
import { connect } from 'react-redux';
import { Locale, useLocale, OutputChannels, filterModelsByOutputChannel } from 'src/modules/locale';
import { MobileNavigation } from 'src/modules/layout/components/header/components/MobileNavigation';
import { Logo } from './components/Logo';
import { breakpoints } from 'src/utils/breakpoints';
import { fetchHeaderData } from 'src/modules/layout';
import { FormattedMessage, useIntl } from 'react-intl';
import { getImageBreakpointUrl } from 'src/utils/image';

interface LayoutHeaderProps extends Pick<BaseStore, 'header' | 'pages' | 'locale'> {
  fetchHeader: any;
}

let menuCloseTimer;

const getScrollTop =
  typeof window !== 'undefined'
    ? () =>
        Math.max(0, window.scrollY || document.body.scrollTop || document.documentElement.scrollTop)
    : () => 0;

const professionalPageCardSlugs = ['downloads', 'pcon', 'projects'];

const LayoutHeader: FC<LayoutHeaderProps> = (props) => {
  const navBarContainerRef = useRef<HTMLDivElement>(null);
  const [navigationVisible, setNavigationVisible] = useState<boolean>(true);
  const [isTransparent, setTransparent] = useState<boolean>(true);
  const [activeMenu, setActiveMenu] = useState<string>();
  const [searchIsOpen, setSearchIsOpen] = useState<boolean>(false);
  const [mobileNavigationVisible, setMobileNavigationVisible] = useState<boolean>(false);
  const [hasHeroHoverStyle, setHeroHoverStyle] = useState<boolean>(false);
  const [hasSemiTransparentStyle, setSemitransparentStyle] = useState<boolean>(false);

  const {
    getLocalizedObject,
    setLocale,
    europe_url,
    australia_url,
    usa_url,
    outputChannel,
  } = useLocale();
  const intl = useIntl();
  const lastScrollTopRef = useRef<number>(getScrollTop());

  const {
    locale: { locale },
    fetchHeader,
  } = props;

  const localeKey = locale?.toUpperCase() as FlagWithLocaleFlagType;

  let designers;
  let designersCards;
  let productCategories;
  let articleCategories;
  let articles;
  let heroBox;

  if (props.header) {
    const header = getLocalizedObject(props.header);
    designers = header.designers;
    designersCards = header.designerCards;
    productCategories = header.productCategories;
    articleCategories = header.articleCategories;
    articles = header.articles;
    heroBox = header.heroBox;
  } else {
    designers = [];
    designersCards = [];
    productCategories = [];
    articleCategories = [];
    articles = [];
    heroBox = null;
  }

  const products = props.header?.products
    .filter((p) => p.channels.includes(outputChannel as OutputChannels))
    .map((p) => getLocalizedObject(p));

  const aboutPage = getLocalizedObject(props.header?.aboutPage);
  const professionalsPage = getLocalizedObject(props.header?.professionalsPage);

  const professionalsByChannel = filterModelsByOutputChannel(
    professionalsPage?.dynamic_content,
    outputChannel
  ) as any;

  const professionalsCards =
    professionalsByChannel.filter((dc) => professionalPageCardSlugs.includes(dc.link)) || [];

  const aboutByChannel = filterModelsByOutputChannel(
    aboutPage?.dynamic_content,
    outputChannel
  ) as any;

  const aboutCards = _.filter(aboutByChannel, (dc: any) => !!dc?.link);
  if (aboutCards.length > 3) aboutCards.splice(3, aboutCards.length - 3);

  const openSearch = useCallback(() => {
    setSearchIsOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchIsOpen(false);
  }, []);

  const openMobileNavigation = useCallback(() => {
    setMobileNavigationVisible(true);
  }, []);

  const closeMobileNavigation = useCallback(() => {
    setMobileNavigationVisible(false);
  }, []);

  const closeMenu = () => {
    if (menuCloseTimer) {
      clearTimeout(menuCloseTimer);
    }

    menuCloseTimer = setTimeout(() => setActiveMenu(null), 100);
  };

  const openMenu = (menuName: string) => {
    if (menuCloseTimer) {
      clearTimeout(menuCloseTimer);
    }
    setActiveMenu(menuName);
  };

  const scrollUpdater = useCallback(() => {
    const currentScrollTop = getScrollTop();

    if (currentScrollTop < lastScrollTopRef.current) {
      setNavigationVisible(true);
    } else if (currentScrollTop > lastScrollTopRef.current) {
      setNavigationVisible(false);
      setActiveMenu(null);
    }

    if (heroBox) {
      if (navBarContainerRef.current) {
        const navBoxHeight = navBarContainerRef.current.offsetHeight;

        if (currentScrollTop + navBoxHeight / 2 > heroBox.height) {
          if (hasHeroHoverStyle) {
            setHeroHoverStyle(false);
            setSemitransparentStyle(true);
          }
        } else {
          if (!hasHeroHoverStyle) {
            setHeroHoverStyle(true);
            setSemitransparentStyle(false);
          }
        }
      }
    } else {
      if (hasHeroHoverStyle) {
        setHeroHoverStyle(false);
      }
      const navBoxHeight = navBarContainerRef?.current?.offsetHeight;

      if (currentScrollTop > navBoxHeight / 2) {
        setSemitransparentStyle(true);
      } else {
        setSemitransparentStyle(false);
      }
    }

    lastScrollTopRef.current = currentScrollTop;
  }, [hasHeroHoverStyle, heroBox]);

  useEffect(() => {
    scrollUpdater();

    window.addEventListener('scroll', () => {
      scrollUpdater();
    });
    window.addEventListener('resize', () => {
      scrollUpdater();
    });

    return () => {
      scrollUpdater();
      window.removeEventListener('scroll', scrollUpdater);
      window.removeEventListener('resize', scrollUpdater);
    };
  }, [scrollUpdater]);

  useEffect(() => {
    if (!props.header?.isLoaded && !props.header?.isLoading) {
      fetchHeader(outputChannel);
    }
  }, [fetchHeader, outputChannel, props.header?.isLoaded, props.header?.isLoading]);

  // useEffect(() => {
  //   console.log(props.pages?.currentPageKey);
  //   if (props.pages?.currentPageKey) {
  //     scrollUpdater();
  //   }
  // }, [props.pages?.currentPageKey, scrollUpdater]);

  // this hack is used to set a different key for the parent container
  // because the server side rendering creates a rendering bug that
  // breaks the interface
  const [hack, setHack] = useState('');
  useEffect(() => setHack('hack'), []);

  const onClickLanguage = (locale) => {
    if (outputChannel === OutputChannels.DE) {
      setLocale(locale);
    }
  };

  const magazineLinks = ([
    {
      url: '/magazine',
      text: intl.formatMessage({ id: 'page/magazine/listing/all_topics' }),
    },
  ] as NavMainMenuLink[])
    .concat(
      articleCategories.map((ac) => ({
        url: `/magazine?category=${encodeURIComponent(ac.name)}`,
        text: ac.name,
      }))
    )
    .concat([
      {
        url: '',
        text: '',
        linkType: 'divider',
      },
    ])
    .concat([
      {
        url: '/magazine/magazines-and-brochures',
        text: intl.formatMessage({ id: 'page/magazinesAndBrochures/title' }),
      },
    ])
    .concat([
      {
        url: '/magazine/instagram',
        text: intl.formatMessage({ id: 'breadcrumbs/magazine/instagram' }),
      },
    ]);

  return (
    <Media>
      {({ currentBreakpoint } = { currentBreakpoint: 'xs' }) => {
        return (
          <React.Fragment key={`LayoutHeader-${currentBreakpoint}-${hack}`}>
            <SearchDrawer isOpen={searchIsOpen} onClose={closeSearch} />
            <MobileNavigation
              locale={locale}
              isOpen={mobileNavigationVisible}
              onClose={closeMobileNavigation}
              productCategories={productCategories}
              magazineLinks={magazineLinks}
              openSearch={openSearch}
              setLocale={onClickLanguage}
              usaUrl={usa_url}
              australiaUrl={australia_url}
              europeUrl={europe_url}
            />

            <div
              onMouseEnter={() => setTransparent(false)}
              onMouseLeave={() => setTransparent(true)}
              className={classnames(
                styles.container,
                navigationVisible ? styles.visible : styles.hidden,
                isTransparent ? styles.transparent : styles.opaque
              )}>
              <div
                className={classnames(
                  styles.navBarContainer,
                  isTransparent && hasHeroHoverStyle && styles.heroHover,
                  hasSemiTransparentStyle && !hasHeroHoverStyle && styles.semiTransparent
                )}
                ref={navBarContainerRef}>
                {breakpoints[currentBreakpoint] < breakpoints.md && (
                  <div
                    key="mobile-main-menu-button"
                    className={styles.mobileMainMenuButton}
                    onClick={openMobileNavigation}>
                    <GlobalIcon image={IconImage.HamburgerMenu} />
                  </div>
                )}
                <Logo className={styles.logo} />
                {breakpoints[currentBreakpoint] < breakpoints.md ? (
                  <div key="top-buttons-mobile" className={styles.mobileTopButtons}>
                    <div className={styles.mobileTopButton} onClick={openSearch}>
                      <GlobalIcon
                        size="custom"
                        className={styles.mobileTopButtonIcon}
                        image={IconImage.Search}
                      />
                    </div>
                    <Link as="/store" href="/store">
                      <a className={styles.mobileTopButton}>
                        <GlobalIcon
                          size="custom"
                          className={styles.mobileTopButtonIcon}
                          image={IconImage.StorePin}
                        />
                      </a>
                    </Link>
                    <Link as="/professionals/contact" href="/professionals/contact">
                      <a className={styles.mobileTopButton}>
                        <GlobalIcon
                          size="custom"
                          className={styles.mobileTopButtonIcon}
                          image={IconImage.Contact}
                        />
                      </a>
                    </Link>
                  </div>
                ) : (
                  <React.Fragment key="desktop-top-buttons">
                    <div className={styles.mainButtonsContainer}>
                      <NavMenuButton
                        name="products"
                        href="/products"
                        closeMenu={closeMenu}
                        openMenu={openMenu}
                        activeMenu={activeMenu}
                        isWhite={hasHeroHoverStyle && isTransparent}>
                        <FormattedMessage id="header/menu_button/products" />
                      </NavMenuButton>
                      <NavMenuButton
                        name="magazine"
                        href="/magazine"
                        closeMenu={closeMenu}
                        openMenu={openMenu}
                        activeMenu={activeMenu}
                        isWhite={hasHeroHoverStyle && isTransparent}>
                        <FormattedMessage id="header/menu_button/magazine" />
                      </NavMenuButton>
                      <NavMenuButton
                        name="designers"
                        href="/designers"
                        closeMenu={closeMenu}
                        openMenu={openMenu}
                        activeMenu={activeMenu}
                        isWhite={hasHeroHoverStyle && isTransparent}>
                        <FormattedMessage id="header/menu_button/designers" />
                      </NavMenuButton>
                      <NavMenuButton
                        name="professionals"
                        href="/professionals"
                        closeMenu={closeMenu}
                        openMenu={openMenu}
                        activeMenu={activeMenu}
                        isWhite={hasHeroHoverStyle && isTransparent}>
                        <FormattedMessage id="header/menu_button/professionals" />
                      </NavMenuButton>
                      <NavMenuButton
                        name="about"
                        href="/about"
                        closeMenu={closeMenu}
                        openMenu={openMenu}
                        activeMenu={activeMenu}
                        isWhite={hasHeroHoverStyle && isTransparent}>
                        <FormattedMessage id="header/menu_button/about" />
                      </NavMenuButton>
                    </div>
                    <div className={styles.secondaryButtonsContainer}>
                      {currentBreakpoint === 'md' ? (
                        <div
                          key="secondary-buttons-md"
                          className={styles.secondaryMenuButton}
                          onClick={() => openMenu('secondaryMenu')}
                          onMouseLeave={closeMenu}>
                          <GlobalIcon image={IconImage.HamburgerMenu} />
                        </div>
                      ) : (
                        <React.Fragment key="secondary-buttons-lg">
                          <div className={styles.secondaryButton} onClick={openSearch}>
                            <GlobalIcon
                              image={IconImage.Search}
                              size="custom"
                              className={styles.searchIcon}
                            />
                            <FormattedMessage id="header/menu_button/search" />
                            <div className={styles.secondaryButtonHoverEffect} />
                          </div>
                          <Link as="/store" href="/store">
                            <a className={styles.secondaryButton}>
                              <FormattedMessage id="header/menu_button/stores" />
                              <div className={styles.secondaryButtonHoverEffect} />
                            </a>
                          </Link>
                          <Link as="/professionals/downloads" href="/professionals/downloads">
                            <a
                              className={classnames(
                                styles.secondaryButton,
                                styles.largeScreenSecondaryButton
                              )}>
                              <FormattedMessage id="header/menu_button/downloads" />
                              <div className={styles.secondaryButtonHoverEffect} />
                            </a>
                          </Link>
                          <Link as="/professionals/contact" href="/professionals/contact">
                            <a className={styles.secondaryButton}>
                              <FormattedMessage id="header/menu_button/contact" />
                              <div className={styles.secondaryButtonHoverEffect} />
                            </a>
                          </Link>
                          <div
                            className={styles.secondaryButton}
                            onMouseEnter={() => openMenu('locale')}
                            onMouseLeave={() => closeMenu()}>
                            <FlagWithLocale flag={localeKey} label={localeKey} />
                            <div className={styles.secondaryButtonHoverEffect} />
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
              <div className={styles.menuMainContainer}>
                <NavMainMenu
                  activeMenu={activeMenu}
                  name="products"
                  closeMenu={closeMenu}
                  openMenu={openMenu}
                  title={intl.formatMessage({ id: 'header/menu_title/view_by_category' })}
                  links={productCategories.map((pc) => ({
                    url: `/products/${encodeURIComponent(pc.slug)}`,
                    text: pc.name,
                    subcategories: pc.product_subcategories,
                  }))}
                  images={products?.map((p) => ({
                    imageUrl: getImageBreakpointUrl('md', p?.assets?.[0]?.file)?.url,
                    caption: p.name,
                    linkUrl: `/products/${encodeURIComponent(p.categorySlug)}/${encodeURIComponent(
                      p.slug
                    )}`,
                  }))}
                  objectFit="contain"
                />

                <NavMainMenu
                  activeMenu={activeMenu}
                  name="magazine"
                  closeMenu={closeMenu}
                  openMenu={openMenu}
                  title={intl.formatMessage({ id: 'header/menu_title/view_by_category' })}
                  links={magazineLinks}
                  images={articles.map((a) => ({
                    imageUrl: getImageBreakpointUrl('md', a?.hero?.image)?.url,
                    caption: a.hero?.title,
                    linkUrl: `/magazine/${a.slug}`,
                  }))}
                />
                <NavMainMenu
                  activeMenu={activeMenu}
                  name="designers"
                  closeMenu={closeMenu}
                  openMenu={openMenu}
                  title={intl.formatMessage({ id: 'header/menu_title/our_designers' })}
                  moreLink={{ url: '/designers', text: 'All Designers' }}
                  links={designers.map((d) => ({
                    url: `/designers/${encodeURIComponent(d.slug)}`,
                    text: d.name,
                  }))}
                  images={designersCards.map((d) => ({
                    imageUrl: getImageBreakpointUrl('md', d?.assets?.[0]?.file)?.url,
                    caption: d.name,
                    linkUrl: `/designers/${encodeURIComponent(d.slug)}`,
                  }))}
                />
                <NavMainMenu
                  activeMenu={activeMenu}
                  name="professionals"
                  closeMenu={closeMenu}
                  openMenu={openMenu}
                  title={intl.formatMessage({ id: 'header/menu_title/view_by_category' })}
                  links={
                    professionalsByChannel.map((dc) => ({
                      url: `/professionals/${encodeURIComponent(dc.link)}`,
                      text: dc.title,
                    })) || []
                  }
                  images={professionalsCards.map((p) => ({
                    imageUrl: getImageBreakpointUrl('md', p?.image)?.url,
                    caption: p.title,
                    linkUrl: `/professionals/${encodeURIComponent(p.link)}`,
                  }))}
                />
                <NavMainMenu
                  activeMenu={activeMenu}
                  name="about"
                  closeMenu={closeMenu}
                  openMenu={openMenu}
                  title={intl.formatMessage({ id: 'header/menu_title/view_by_category' })}
                  links={
                    aboutByChannel.map((dc) => ({
                      url: `/about/${encodeURIComponent(dc.link)}`,
                      text: dc.title,
                    })) || []
                  }
                  images={aboutCards.map((dc) => ({
                    imageUrl: getImageBreakpointUrl('md', dc?.image)?.url,
                    caption: dc.title,
                    linkUrl: `/about/${encodeURIComponent(dc.link)}`,
                  }))}
                />
                <NavMenu
                  className={styles.secondaryMenu}
                  activeMenu={activeMenu}
                  name="secondaryMenu"
                  closeMenu={closeMenu}
                  openMenu={openMenu}>
                  <div className={styles.secondaryMenuItem} onClick={openSearch}>
                    <GlobalIcon
                      image={IconImage.Search}
                      className={styles.secondaryMenuItemIcon}
                      size="custom"
                    />
                    <FormattedMessage id="header/menu_button/search" />
                  </div>
                  <a className={styles.secondaryMenuItem} href="/store">
                    <GlobalIcon
                      image={IconImage.StorePin}
                      className={styles.secondaryMenuItemIcon}
                      size="custom"
                    />
                    <FormattedMessage id="header/menu_button/stores" />
                  </a>
                  <a className={styles.secondaryMenuItem} href="/professionals/contact">
                    <GlobalIcon
                      image={IconImage.Contact}
                      className={styles.secondaryMenuItemIcon}
                      size="custom"
                    />
                    <FormattedMessage id="header/menu_button/contact" />
                  </a>
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="DE"
                    href={europe_url}
                    locale={Locale.DE}
                    label={intl.formatMessage({ id: 'locale/de' })}
                    onClick={() => {
                      onClickLanguage(Locale.DE);
                    }}
                    selected={locale === Locale.DE}
                    shallow={outputChannel === OutputChannels.DE}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="EN"
                    href={europe_url}
                    locale={Locale.EN}
                    label={intl.formatMessage({ id: 'locale/en' })}
                    onClick={() => {
                      onClickLanguage(Locale.EN);
                    }}
                    selected={locale === Locale.EN}
                    shallow={outputChannel === OutputChannels.DE}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="US"
                    href={usa_url}
                    locale={Locale.EN}
                    selected={locale === Locale.US}
                    label={intl.formatMessage({ id: 'locale/us' })}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="AU"
                    href={australia_url}
                    locale={Locale.EN}
                    selected={locale === Locale.AU}
                    label={intl.formatMessage({ id: 'locale/au' })}
                  />
                </NavMenu>
                <NavMenu
                  className={styles.secondaryMenu}
                  activeMenu={activeMenu}
                  name="locale"
                  closeMenu={closeMenu}
                  openMenu={openMenu}>
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="DE"
                    href={europe_url}
                    locale={Locale.DE}
                    label={intl.formatMessage({ id: 'locale/de' })}
                    onClick={() => {
                      onClickLanguage(Locale.DE);
                    }}
                    selected={locale === Locale.DE}
                    shallow={outputChannel === OutputChannels.DE}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="EN"
                    href={europe_url}
                    locale={Locale.EN}
                    label={intl.formatMessage({ id: 'locale/en' })}
                    onClick={() => {
                      onClickLanguage(Locale.EN);
                    }}
                    selected={locale === Locale.EN}
                    shallow={outputChannel === OutputChannels.DE}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="US"
                    href={usa_url}
                    locale={Locale.EN}
                    selected={locale === Locale.US}
                    label={intl.formatMessage({ id: 'locale/us' })}
                  />
                  <FlagWithLocale
                    className={styles.secondaryMenuItem}
                    flag="AU"
                    href={australia_url}
                    locale={Locale.EN}
                    selected={locale === Locale.AU}
                    label={intl.formatMessage({ id: 'locale/au' })}
                  />
                </NavMenu>
              </div>
            </div>
          </React.Fragment>
        );
      }}
    </Media>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchHeader: (outputChannel) => dispatch(fetchHeaderData(outputChannel)),
    dispatch,
  };
};

const mapStateToProps = (state: BaseStore) => {
  return { header: state.header, pages: state?.pages, locale: state.locale };
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutHeader);
